/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo } from 'react';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { BaseServiceComponentProps } from '@/pages/benchmark/types/commom';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { getCompleteDateValue } from '@/pages/dashboard/utils/utils';
import { DATA_TYPE_OBJECT } from '@home/utils/common';
import generateDateRanges from '@home/utils/dbDataFormat/generateDateRanges';
import moment from 'moment';
import { DBO_V } from '@op/utils/format';

export interface BIOpinionsMetricService extends BaseServiceComponentProps {
  metric: string;
  gameCodes: string[];
  [key: string]: any;
}

export default function BIOpinionsMetricService(props: BIOpinionsMetricService) {
  const {
    style,
    className,
    dataKey = 'CommonData',
    api,
    children,
    filter,
    metrics,
    gameCodes,
    extraParams = {},
    formatData: propsFormatData,
    data,
    filterConfig,
    paymentType,
    metricKeys,
    metricKey,
    subtitleKey,
    changeData,
    ...restProps
  } = props || {};

  const filterVal = useMemo(
    () => ({ ...filter }),
    [filter],
  );

  const formatData = (data: any, filterVal: any) => {
    if (propsFormatData && typeof propsFormatData === 'function') {
      return propsFormatData?.(data, filterVal);
    }
  
    const { timeline } = data?.list?.[0] || [];
    const cardList: any[] = [];
    let trendList: any[] = [];
  
    if (timeline?.length > 0) {
      const newList = timeline?.filter((item: { key: string; }) => metricKeys.includes(item?.key)) || [];
      
      const { start_time, end_time, date_type, isSingle, isChange } = filterVal || {};
      let startDate = dateTimeFormat(start_time, 'day');
      const timeType = DATA_TYPE_OBJECT[date_type];
      
      // 补齐逻辑
      if (isSingle) {
        const rangeTimeArr = getCompleteDateValue([start_time, end_time], timeType, { offsetDay: 5 });
        startDate = dateTimeFormat(rangeTimeArr[0], 'day');
      }
  
      // 补齐日期和排序
      const dateRange = generateDateRanges(startDate, end_time);
      
      // 性能优化：预计算总值，避免在循环中重复计算
      const totalRangeValue = newList.reduce((sum: number, item: { review: number; }) => sum + item.review, 0);
      const rangeValue = newList
        .filter((item: { key: string; }) => item.key === metricKey)
        .reduce((sum: number, item: { review: number; }) => sum + item.review, 0);
      const cardRangeValue = ((totalRangeValue === 0 || rangeValue === 0) ? undefined : rangeValue / totalRangeValue);
  
      // 性能优化：按日期分组数据，避免每次都过滤整个数组
      const dataByDate = new Map<string, any[]>();
      newList.forEach((item: { time: moment.MomentInput; }) => {
        const dateKey = moment(item.time).format('YYYY-MM-DD');
        if (!dataByDate.has(dateKey)) {
          dataByDate.set(dateKey, []);
        }
        dataByDate.get(dateKey)!.push(item);
      });
  
      // 性能优化：创建按key分组的查找函数
      const getItemByKeyFromDate = (dateData: any[], key: string) => dateData.find(item => item.key === key);
  
      trendList = dateRange?.map((dateString: string, index: number) => {
        const currentList = dataByDate.get(dateString) || [];
        
        let total = 0;
        let currentValue = 0;
        
        // 性能优化：减少find操作，直接在forEach中处理
        metricKeys.forEach((key: string, keyIndex: number) => {
          const item = getItemByKeyFromDate(currentList, key);
          const currValue = item?.review || 0;
          total += currValue;
          if (keyIndex === 0) {
            currentValue = currValue;
          }
        });
  
        const cardValue = ((total === 0 || currentValue === 0) ? undefined : currentValue / total);
  
        // 最新一天处理
        if (index === dateRange?.length - 1) {
          const previousDateData = dataByDate.get(dateRange[index - 1]) || [];
          const oldDay = getItemByKeyFromDate(previousDateData, metricKey) || {};
          
          cardList.push({
            metrics_name: metricKey,
            value: isSingle ? cardValue : cardRangeValue,
            dod: {
              show_value: DBO_V.qoq(currentValue, oldDay?.review, 6) / 100,
              this_time: dateString,
            }
          });
        }
  
        return {
          date: dateString,
          metrics: {
            [metricKey]: {
              value: cardValue,
              last_value: '',
              last_month_value: '',
              last_year_value: '',
            },
          },
        };
      }) || [];
  
      // 处理日期变更逻辑（移到循环外面，避免重复执行）
      if (!isChange) {
        changeData(subtitleKey, dateRange[dateRange.length - 1] || '');
      } else {
        changeData(subtitleKey, '');
      }
    }
  
    return {
      card_list: cardList,
      trend_list: trendList,
    };
  };

  return (
    <div className={className} style={style}>
      <Service
        api={api}
        data={{ ...props.data }}
        dataKey={dataKey}
        filterLoading={false}
        formatData={formatData}
        filterVal={filterVal}
        trackData={[]}
        beforeRequest={(data) => {
          const { filterVal } = data;
          const { start_time, end_time, isSingle, date_type } = filterVal || {};
          let startDate = dateTimeFormat(start_time, 'day');
          const timeType = DATA_TYPE_OBJECT[date_type];
          // 补齐逻辑
          if (isSingle) {
            const rangeTimeArr = getCompleteDateValue([start_time, end_time], timeType, { offsetDay: 5 });
            startDate = dateTimeFormat(rangeTimeArr[0], 'day');
          }
          const params = {
            start_time: `${startDate  } 00:00:00`,
            end_time: `${dateTimeFormat(end_time, 'day')  } 23:59:59`,
            "date_type": "daily",
            "language": [],
            "sentiment": [],
            "topic": [],
            "topic_rule": "include",
            "reviews_rating": [],
            "steam_ids": [],
            "starred_tag": 0,
            "refunded": 0,
            "free_played": 0,
            "funnys": [],
            "helpfuls": [],
            "record_times": [],
            "review_times": [],
            "early_access": 0,
            "is_valid": [],
            "keyword": [],
            "like_math_keywords": [],
            "like_math_exclude_keywords": [],
            "entity_type": "pc",
            "id_type": "edition_id",
            "channel": [
              "steam"
            ],
            "channel_type": "comments",
            "steam_filter_type": "steam",
            "channels": [
              "steam"
            ],
            "metrics": [
              "recommend",
              "non_recommend"
            ],
            "ret_metrics": [
              "recommend",
              "non_recommend"
            ],
            "types": [
              "total",
              "slice"
            ],
            need_link: true,
            ...extraParams,
          };

          return params;
        }}
      >
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...(restProps || {}),
            ...data,
            filter,
          }),
        )}
      </Service>
    </div>
  );
}
